import React from 'react';

import { useTranslation } from '@/i18n';
import { TransferPanel } from '@/components/ui/panels/TransferPanel';
import { Text } from '@/components/ui/text/Text';
import { colors } from '@/assets/styles/colors';
import type { UseMonthlyCutGroupsTransferResult } from '@/hooks/attendance/monthlyCuts/useMonthlyCutGroupsTransfer';


interface CreateMonthlyCutGroupsFormProps {
  groups: UseMonthlyCutGroupsTransferResult;
  // Mensaje de error (ya traducido) cuando no hay agrupaciones asignadas al guardar.
  errorMessage?: string;
}

// Las agrupaciones se cargan completas en memoria (sin scroll infinito de servidor),
// por lo que los refs del sentinel/contenedor del TransferPanel no se utilizan.
const noopRef: React.RefCallback<HTMLDivElement> = () => {};

export const CreateMonthlyCutGroupsForm: React.FC<CreateMonthlyCutGroupsFormProps> = ({
  groups,
  errorMessage,
}) => {
  const { t: translate } = useTranslation();

  const searchPlaceholder = translate('attendance:monthlyCutCreate.groups.searchPlaceholder');

  return (
    <div className="flex flex-col gap-4" data-testid="create-monthly-cut-groups-form">
      <TransferPanel
        keyExtractor={(group) => group.id}
        renderTitle={(group) => group.name}
        leftPanel={{
          title: translate('attendance:monthlyCutCreate.groups.availableTitle'),
          items: groups.filteredAvailable,
          allItems: groups.availableAll,
          isLoading: groups.isLoading,
          totalRecords: groups.availableAll.length,
          searchValue: groups.availableSearch,
          onSearch: groups.onAvailableSearch,
          searchPlaceholder,
          selectedIds: groups.selectedAvailableIds,
          onToggleSelect: groups.onToggleAvailable,
          onSelectAll: groups.onSelectAllAvailable,
          isAllSelected: groups.isAllAvailableSelected,
          isIndeterminate: groups.isAvailableIndeterminate,
          onDoubleClick: groups.onDoubleClickAvailable,
          sentinelRef: noopRef,
          scrollContainerRef: noopRef,
          footerText: translate('attendance:monthlyCutCreate.groups.footer', {
            selected: groups.selectedAvailableIds.size,
            total: groups.availableAll.length,
          }),
          'data-testid': 'monthly-cut-available-groups',
        }}
        rightPanel={{
          title: translate('attendance:monthlyCutCreate.groups.assignedTitle'),
          items: groups.filteredAssigned,
          allItems: groups.assigned,
          isLoading: false,
          totalRecords: groups.assigned.length,
          searchValue: groups.assignedSearch,
          onSearch: groups.onAssignedSearch,
          searchPlaceholder,
          selectedIds: groups.selectedAssignedIds,
          onToggleSelect: groups.onToggleAssigned,
          onSelectAll: groups.onSelectAllAssigned,
          isAllSelected: groups.isAllAssignedSelected,
          isIndeterminate: groups.isAssignedIndeterminate,
          onDoubleClick: groups.onDoubleClickAssigned,
          sentinelRef: noopRef,
          scrollContainerRef: noopRef,
          footerText: translate('attendance:monthlyCutCreate.groups.footer', {
            selected: groups.selectedAssignedIds.size,
            total: groups.assigned.length,
          }),
          'data-testid': 'monthly-cut-assigned-groups',
        }}
        onAdd={groups.onAdd}
        onRemove={groups.onRemove}
        addLabel={translate('common:actions.add')}
        removeLabel={translate('common:actions.remove')}
        isAddDisabled={groups.selectedAvailableIds.size === 0}
        isRemoveDisabled={groups.selectedAssignedIds.size === 0}
        data-testid="monthly-cut-groups-transfer"
      />

      {errorMessage && (
        <Text
          variant="small-regular"
          color={colors.feedback.error400}
          data-testid="monthly-cut-groups-error"
        >
          {errorMessage}
        </Text>
      )}
    </div>
  );
};

export default CreateMonthlyCutGroupsForm;
